import { locationIdFormat, productIdFormat } from "@redotech/redo-model/ids";
import { GeneralProductFamilyField } from "@redotech/redo-model/inventory/general-listing/general-listing";
import { GeneralProductField } from "@redotech/redo-model/inventory/general-listing/general-listing-variant";
import { createEnum } from "@redotech/zod/zod-enum";
import * as z from "zod/v4";

// Bulk-updatable fields drawn from the existing source-of-truth enums where possible.
// Product-family-level: vendor (GeneralProductFamilyField), tags, productType
// Product-level: weight/hsCode/originCountry (GeneralProductField), cost, taxable, taxCode, physicalItem
const bulkProductDetailFields = [
  GeneralProductFamilyField.vendor,
  GeneralProductField.weight,
  GeneralProductField.hsCode,
  GeneralProductField.originCountry,
  "tags",
  "productType",
  "cost",
  "taxable",
  "taxCode",
  "physicalItem",
  "locations",
] as const;

// Type-check: ensure the enum values we reference haven't drifted from the string literals
// the rest of the codebase expects.
const _vendorCheck: "vendor" = GeneralProductFamilyField.vendor;
const _weightCheck: "weight" = GeneralProductField.weight;
const _hsCodeCheck: "hsCode" = GeneralProductField.hsCode;
const _originCountryCheck: "originCountry" = GeneralProductField.originCountry;
void _vendorCheck;
void _weightCheck;
void _hsCodeCheck;
void _originCountryCheck;

export const productDetailsFieldType = createEnum(bulkProductDetailFields);
export type ProductDetailsFieldType = z.infer<typeof productDetailsFieldType>;

export const tagUpdateMode = createEnum(["add", "replace", "remove"]);
export type TagUpdateMode = z.infer<typeof tagUpdateMode>;

export const locationUpdateMode = createEnum(["add", "remove", "replace"]);
export type LocationUpdateMode = z.infer<typeof locationUpdateMode>;

const weightUnit = z.enum(["g", "kg", "oz", "lb"]);
export type WeightUnit = z.infer<typeof weightUnit>;

export const priceAdjustmentMode = createEnum([
  "set",
  "adjustAmount",
  "adjustPercent",
]);
export type PriceAdjustmentMode = z.infer<typeof priceAdjustmentMode>;

export const bulkUpdateProductDetails = {
  name: "bulkUpdateProductDetails" as const,
  input: z.object({
    productIds: z.array(productIdFormat.schema),
    field: productDetailsFieldType,
    stringValue: z.string().optional(),
    numericValue: z.number().optional(),
    booleanValue: z.boolean().optional(),
    tags: z.array(z.string()).optional(),
    tagUpdateMode: tagUpdateMode.optional(),
    weightValue: z.number().optional(),
    weightUnit: weightUnit.optional(),
    priceAdjustmentMode: priceAdjustmentMode.optional(),
    percentValue: z.number().optional(),
    locationIds: z.array(locationIdFormat.schema).optional(),
    locationUpdateMode: locationUpdateMode.optional(),
  }),
  output: z.object({ updatedCount: z.number().int() }),
};
